import React, { useState, useEffect } from "react";
import { Form, Input, Button, Select, Switch } from "antd";
import EntraSsoModalWrapper from "./EntraSsoModal.style";
import { entraSsoService } from "@iso/services";

const { Option } = Select;

const EntraSsoModal = (props) => {
  const {
    visibleModal,
    setVisibleModal,
    editingConfig,
    getConfigs,
    setEditingConfig,
    corporateGroups,
    properties,
    roleTypes,
  } = props;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [autoProvision, setAutoProvision] = useState(false);
  const [enforceSso, setEnforceSso] = useState(false);

  const isEdit = !!editingConfig.id;

  useEffect(() => {
    setFields([
      { name: "corporate_group_id", value: editingConfig.corporate_group_id ?? undefined },
      { name: "entra_tenant_id", value: editingConfig.entra_tenant_id ?? "" },
      { name: "allowed_email_domains", value: editingConfig.allowed_email_domains ?? [] },
      { name: "default_role_type", value: editingConfig.default_role_type ?? undefined },
      { name: "default_property_id", value: editingConfig.default_property_id ?? undefined },
    ]);
    // New configs default to enabled; edits reflect the stored flags.
    setEnabled(editingConfig.id ? !!editingConfig.enabled : true);
    setAutoProvision(!!editingConfig.auto_provision);
    setEnforceSso(!!editingConfig.enforce_sso);
  }, [editingConfig, visibleModal]);

  const handleSubmit = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        onStore(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setLoading(false);
      });
  };

  const onStore = (values) => {
    const data = {
      ...values,
      enabled: enabled ? 1 : 0,
      auto_provision: autoProvision ? 1 : 0,
      enforce_sso: enforceSso ? 1 : 0,
    };
    entraSsoService
      .storeOrUpdate(data, editingConfig.id ?? null)
      .then((res) => {
        setLoading(false);
        if (res && res.code === "0000") {
          getConfigs();
          onCancelModal();
        }
      });
  };

  const onCancelModal = () => {
    setEditingConfig({});
    setVisibleModal(false);
  };

  return (
    <EntraSsoModalWrapper
      className="user-access-modal"
      visible={visibleModal}
      title={isEdit ? "Edit SSO Configuration" : "Add SSO Configuration"}
      onCancel={onCancelModal}
      maskClosable={false}
      footer={[
        <Button key="back" onClick={onCancelModal} className="cancel-button">
          Cancel
        </Button>,
        <Button
          key="submit"
          className="btn-success"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} fields={fields} layout="vertical">
        <Form.Item
          name="corporate_group_id"
          className="isoInputWrapper"
          label="Corporate Group"
          rules={[
            { required: true, message: "Please select a corporate group!" },
          ]}
        >
          <Select
            size="large"
            showSearch
            placeholder="Select corporate group"
            optionFilterProp="children"
          >
            {corporateGroups.map((g) => (
              <Option key={g.id} value={g.id}>
                {g.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="entra_tenant_id"
          className="isoInputWrapper"
          label="Entra Tenant ID"
          rules={[
            {
              required: true,
              message: "Please input the Entra tenant (directory) ID!",
            },
          ]}
        >
          <Input
            size="large"
            placeholder="e.g. c1ae2cdc-337c-43c1-a4a3-8b9df552ac00"
          />
        </Form.Item>

        <Form.Item
          name="allowed_email_domains"
          className="isoInputWrapper"
          label="Allowed Email Domains"
          extra="Email-based account linking only works for these domains. Leave empty to require a prior Microsoft link."
        >
          <Select
            size="large"
            mode="tags"
            tokenSeparators={[",", " "]}
            placeholder="e.g. withcott.com.au"
          />
        </Form.Item>

        <Form.Item
          name="default_role_type"
          className="isoInputWrapper"
          label="Default Role (used when auto-provision is on)"
        >
          <Select size="large" allowClear placeholder="Select default role">
            {roleTypes.map((r) => (
              <Option key={r.value} value={r.value}>
                {r.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="default_property_id"
          className="isoInputWrapper"
          label="Default Property (used when auto-provision is on)"
        >
          <Select
            size="large"
            allowClear
            showSearch
            optionFilterProp="children"
            placeholder="Select default property"
          >
            {properties.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div className="switch-row">
          <span className="switch-label">Enabled</span>
          <Switch checked={enabled} onChange={setEnabled} />
        </div>
        <div className="switch-row">
          <span className="switch-label">Auto-provision new users</span>
          <Switch checked={autoProvision} onChange={setAutoProvision} />
        </div>
        <div className="switch-row">
          <span className="switch-label">Enforce SSO</span>
          <Switch checked={enforceSso} onChange={setEnforceSso} />
        </div>
      </Form>
    </EntraSsoModalWrapper>
  );
};

export default EntraSsoModal;
